/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useRef, useState } from 'react';
import { Tag } from 'sharedUi/Tag';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { extractErrorMessage } from '../../../utils/error-handler';
import type { RbacStatus } from '../../../types/rbac';
import SearchInput from '../../../components/common/search-input';

// ---------------------------------------------------------------------------
// Status tag: green ACTIVE / amber otherwise
// ---------------------------------------------------------------------------
export const StatusTag = ({ status }: { status?: RbacStatus }) => (
  <Tag
    value={status || 'ACTIVE'}
    severity={status === 'ACTIVE' || !status ? 'success' : 'warning'}
  />
);

// Small count badge (e.g. permissions-count / sets-count)
export const CountBadge = ({ value }: { value: number }) => (
  <Tag value={String(value)} severity="info" rounded />
);

export const STATUS_OPTIONS = [
  { label: 'ACTIVE', value: 'ACTIVE' },
  { label: 'ARCHIVED', value: 'ARCHIVED' },
];

// Scope: the stored codes (BOTH/UI/API) are opaque to users. Display a readable
// label — "UI & API" for BOTH — while the value sent to the backend stays the code.
export const SCOPE_OPTIONS = [
  { label: 'UI & API', value: 'BOTH' },
  { label: 'UI', value: 'UI' },
  { label: 'API', value: 'API' },
];

export const SCOPE_LABELS: Record<string, string> = {
  BOTH: 'UI & API',
  UI: 'UI',
  API: 'API',
};

/** Human-readable label for a permission scope code (defaults to UI & API). */
export const scopeLabel = (scope?: string) => SCOPE_LABELS[scope || 'BOTH'] || scope || 'UI & API';

// ---------------------------------------------------------------------------
// Toast helper hook
// ---------------------------------------------------------------------------
export const useRbacToast = () => {
  const toast = useRef<Toast>(null);
  const showSuccess = useCallback((detail: string) => {
    toast.current?.show({ severity: 'success', summary: 'Success', detail, life: 3000 });
  }, []);
  const showError = useCallback((err: any, fallback: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: extractErrorMessage(err, fallback),
      life: 8000,
    });
  }, []);
  return { toast, showSuccess, showError };
};

// ---------------------------------------------------------------------------
// Dialog footer (Cancel / Save)
// ---------------------------------------------------------------------------
export const DialogFooter = ({
  onCancel,
  onSave,
  saving,
  isEdit,
}: {
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  isEdit: boolean;
}) => (
  <div className="flex justify-end gap-2 pt-1">
    <Button label="Cancel" severity="secondary" outlined onClick={onCancel} disabled={saving} />
    <Button
      label={saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
      icon={saving ? 'pi pi-spin pi-spinner' : undefined}
      onClick={onSave}
      disabled={saving}
    />
  </div>
);

// ---------------------------------------------------------------------------
// Reusable labelled field wrappers for the dialogs
// ---------------------------------------------------------------------------
export const Field = ({
  label,
  required,
  error,
  children,
  className,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={['flex flex-col gap-1', className].filter(Boolean).join(' ')}>
    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    {children}
    {error && <span className="text-xs text-rose-600 dark:text-rose-400">{error}</span>}
  </div>
);

export const TextField = ({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) => (
  <input
    type="text"
    value={value}
    disabled={disabled}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className="ds-card w-full px-3 py-2 text-sm text-ink placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
  />
);

export const TextAreaField = ({
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) => (
  <textarea
    value={value}
    rows={rows}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className="ds-card w-full px-3 py-2 text-sm text-ink placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
  />
);

// ---------------------------------------------------------------------------
// Full-page detail view building blocks — replace the old read-only dialogs so
// an RBAC record opens as a whole page (with a back button), like the other
// detail screens in the app.
// ---------------------------------------------------------------------------

/** Page shell: back button + title/subtitle + optional actions, then content. */
export const DetailShell = ({
  backLabel,
  onBack,
  title,
  subtitle,
  actions,
  children,
}: {
  backLabel: string;
  onBack: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="pb-4" data-testid="RbacDetailPage">
    <div className="mb-3 flex items-center gap-2">
      <button aria-label="Back"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 rounded-md border border-line px-2.5 py-1.5 text-sm text-ink-muted transition-colors hover:bg-canvas"
      >
        <i className="pi pi-arrow-left text-xs" /> {backLabel}
      </button>
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
    <div className="mb-4">
      <h2 className="text-lg font-semibold text-ink">{title}</h2>
      {subtitle && <p className="mt-0.5 text-sm text-ink-muted">{subtitle}</p>}
    </div>
    {children}
  </div>
);

/** Full-page form shell: back button + title + content card + Cancel/Save footer.
 *  Replaces the old create/edit dialogs so forms open as a whole page. */
export const FormShell = ({
  backLabel,
  onBack,
  title,
  subtitle,
  onCancel,
  onSave,
  saving,
  isEdit,
  children,
}: {
  backLabel: string;
  onBack: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  isEdit: boolean;
  children: React.ReactNode;
}) => (
  <div className="pb-4" data-testid="RbacFormPage">
    <div className="mb-3 flex items-center gap-2">
      <button aria-label="Back"
        onClick={onBack}
        disabled={saving}
        className="inline-flex items-center gap-1.5 rounded-md border border-line px-2.5 py-1.5 text-sm text-ink-muted transition-colors hover:bg-canvas disabled:opacity-50"
      >
        <i className="pi pi-arrow-left text-xs" /> {backLabel}
      </button>
    </div>
    <div className="mb-4">
      <h2 className="text-lg font-semibold text-ink">{title}</h2>
      {subtitle && <p className="mt-0.5 text-sm text-ink-muted">{subtitle}</p>}
    </div>
    <div className="ds-card p-5">
      {children}
      <div className="mt-5 flex justify-end gap-2 border-t border-line pt-4">
        <Button label="Cancel" severity="secondary" outlined onClick={onCancel} disabled={saving} />
        <Button
          label={saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          icon={saving ? 'pi pi-spin pi-spinner' : undefined}
          onClick={onSave}
          disabled={saving}
        />
      </div>
    </div>
  </div>
);

/** A titled card panel for a group of details. */
export const DetailCard = ({
  title,
  count,
  children,
  className,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`rounded-xl border border-line bg-surface p-4 ${className || ''}`}
  >
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      {count !== undefined && (
        <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-300">
          {count}
        </span>
      )}
    </div>
    {children}
  </div>
);

/** Labelled value; renders "—" when empty. */
export const DetailRow = ({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) => (
  <div className="min-w-0">
    <div className="text-xs text-ink-muted">{label}</div>
    <div className={`break-words text-sm text-ink ${mono ? 'font-mono text-xs' : ''}`}>
      {value ?? '—'}
    </div>
  </div>
);

/** Small titled section wrapping arbitrary content (e.g. a chip list). */
export const DetailSection = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
      {label}
    </div>
    {children}
  </div>
);

/** Chip list for permission keys / set keys, with an optional overflow cap. */
export const Chips = ({
  items,
  tone = 'slate',
  max,
  empty = '—',
}: {
  items: string[];
  tone?: 'slate' | 'emerald' | 'rose' | 'indigo';
  max?: number;
  empty?: string;
}) => {
  if (!items.length) return <span className="text-gray-400">{empty}</span>;
  const visible = max ? items.slice(0, max) : items;
  const overflow = max ? items.length - max : 0;
  const toneClass =
    tone === 'emerald'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-900/40'
      : tone === 'rose'
        ? 'bg-rose-50 text-rose-700 ring-rose-100 dark:bg-rose-900/20 dark:text-rose-300 dark:ring-rose-900/40'
        : tone === 'indigo'
          ? 'bg-indigo-50 text-indigo-700 ring-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-300 dark:ring-indigo-900/40'
          : 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-gray-700 dark:text-gray-200 dark:ring-gray-600';
  return (
    <div className="flex flex-wrap gap-1.5">
      {visible.map((item) => (
        <span
          key={item}
          className={`inline-flex items-center rounded-md px-2 py-0.5 font-mono text-xs ring-1 ring-inset ${toneClass}`}
        >
          {item}
        </span>
      ))}
      {overflow > 0 && (
        <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-100 dark:bg-blue-900/20 dark:text-blue-300">
          +{overflow} more
        </span>
      )}
    </div>
  );
};

// Generic search + refresh + create toolbar used by every tab list
export const ListToolbar = ({
  search,
  onSearch,
  total,
  loading,
  onRefresh,
  onCreate,
  createLabel,
}: {
  search: string;
  onSearch: (v: string) => void;
  total: number;
  loading: boolean;
  onRefresh: () => void;
  onCreate: () => void;
  createLabel: string;
}) => {
  const [, setLocal] = useState(search);
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <SearchInput
        value={search}
        onChange={(v) => { setLocal(v); onSearch(v); }}
        width={220}
      />
      <div className="flex items-center gap-2">
        <span className="text-xs text-ink-muted whitespace-nowrap">
          {total} {total === 1 ? 'record' : 'records'}
        </span>
        <Button
          icon="pi pi-refresh"
          severity="secondary"
          outlined
          onClick={onRefresh}
          loading={loading}
          aria-label="Refresh"
        />
        <Button label={createLabel} icon="pi pi-plus" onClick={onCreate} size="small" />
      </div>
    </div>
  );
};
